import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EventItem, Place } from '../../types/shared/event';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { navigationClient } from '../../lib/navigation';

interface MapViewProps {
  places: Place[];
  config: any;
  translations: Record<string, Record<string, string>>;
  onItemClick: (item: EventItem) => void;
  onPlaceClick: (place: Place) => void;
  mainLocation: {
    name: string;
    latitude: number;
    longitude: number;
    fulladress: string;
  };
  categoryColors: Map<string, string>;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  mapInstanceRef: React.MutableRefObject<google.maps.Map | null>;
}

const MapView: React.FC<MapViewProps> = ({
  places,
  config,
  translations,
  onItemClick,
  onPlaceClick,
  mainLocation,
  categoryColors,
  onCategorySelect,
  onTypeSelect,
  mapInstanceRef
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const mapRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const mainMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const activeMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  const section = navigation?.find(item => {
    const itemSlug = navigationClient.generateSlug(item.names.fr);
    return (
      item.type_rubrique === 'event' && 
      itemSlug === location.pathname.split('/')[2]
    );
  });

  const { isLoaded, error } = useGoogleMaps({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const createSafeEventId = (prefix: string, value: string): string => {
    return `${prefix}_${encodeURIComponent(value).replace(/[^a-zA-Z0-9]/g, '_')}`;
  };

  const getDisplayImage = (image: string): string => {
    if (!image) {
      return section?.replace_img?.[0]?.url || image;
    }
    return image;
  };

  const createSingleShowContent = (show: Place['shows'][0], placeId: string): string => {
    const event = show.events[0];
    const categoryId = createSafeEventId('category', event.category);
    const typeId = event.type ? createSafeEventId('type', event.type) : '';
    const displayImage = getDisplayImage(show.image);

    return `
      <div class="p-4 max-w-xs">
        <div 
          id="${placeId}_image"
          class="w-full h-32 mb-3 cursor-pointer transition-opacity hover:opacity-90"
        >
          <img 
            src="${displayImage}" 
            alt="${show.name}" 
            class="w-full h-full object-cover rounded"
          />
        </div>
        <h3 class="font-bold text-lg mb-2">${show.name}</h3>
        <div class="flex flex-wrap gap-1.5">
          <button
            id="${categoryId}"
            class="inline-block px-2 py-1 text-xs rounded text-white cursor-pointer hover:opacity-80 transition-opacity"
            style="background-color: ${categoryColors.get(event.category) || config['medium-color']}"
          >
            ${getTranslatedCategory(event.category)}
          </button>
          ${event.type ? `
            <button
              id="${typeId}"
              class="inline-block px-2 py-1 text-xs rounded text-white opacity-70 cursor-pointer hover:opacity-80 transition-opacity"
              style="background-color: ${config['medium-color']}"
            >
              ${getTranslatedCategory(event.type)}
            </button>
          ` : ''}
        </div>
        <button
          id="${placeId}"
          class="w-full px-4 py-2 text-white rounded text-sm font-medium transition-colors mt-3"
          style="background-color: ${config['major-color']}"
        >
          ${getTranslatedText('btn_more_info')}
        </button>
      </div>
    `;
  };

  const createMultipleShowsContent = (place: Place, placeId: string): string => {
    const uniqueTypes = Array.from(new Set(place.shows.map(show => show.events[0].type).filter(Boolean)));
    const typeButtons = uniqueTypes.map(type => {
      const typeId = createSafeEventId('type', type);
      return `
        <button
          id="${typeId}"
          class="inline-block px-2 py-1 text-xs rounded text-white opacity-70 cursor-pointer hover:opacity-80 transition-opacity"
          style="background-color: ${config['medium-color']}"
        >
          ${getTranslatedCategory(type)}
        </button>
      `;
    }).join('');

    return `
      <div class="p-4 max-w-xs">
        <h3 class="font-bold text-lg mb-2">${place.name}</h3>
        <p class="text-sm text-gray-600 mb-2">
          ${place.shows.length} ${getTranslatedText('events_shows')}
        </p>
        <div class="flex flex-wrap gap-1.5">
          ${typeButtons}
        </div>
        <button
          id="${placeId}"
          class="w-full px-4 py-2 text-white rounded text-sm font-medium transition-colors mt-3"
          style="background-color: ${config['major-color']}"
        >
          ${getTranslatedText('btn_more_info')}
        </button>
      </div>
    `;
  };

  const setupInfoWindowEventListeners = (placeId: string, place: Place) => {
    window.setTimeout(() => {
      const imageContainer = document.getElementById(`${placeId}_image`);
      if (imageContainer) {
        imageContainer.addEventListener('click', () => {
          if (place.shows.length === 1) {
            analyticsService.trackItemAction(
              section?.id || '',
              place.shows[0].events[0].id,
              'item_click',
              'event',
              {
                item_name: place.shows[0].events[0].name,
                category: place.shows[0].events[0].category,
                type: place.shows[0].events[0].type,
                source: 'map_marker'
              }
            );
            onItemClick(place.shows[0].events[0]);
          } else {
            analyticsService.trackItemAction(
              section?.id || '',
              place.id,
              'place_card_view',
              'place',
              {
                place_name: place.name,
                shows_count: place.shows.length,
                source: 'map_marker'
              }
            );
            onPlaceClick(place);
          }
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
            activeMarkerRef.current = null;
          }
        }, { once: true });
      }

      const mainButton = document.getElementById(placeId);
      if (mainButton) {
        mainButton.addEventListener('click', () => {
          if (place.shows.length === 1) {
            analyticsService.trackItemAction(
              section?.id || '',
              place.shows[0].events[0].id,
              'item_click',
              'event',
              {
                item_name: place.shows[0].events[0].name,
                category: place.shows[0].events[0].category,
                type: place.shows[0].events[0].type,
                source: 'map_marker'
              }
            );
            onItemClick(place.shows[0].events[0]);
          } else {
            analyticsService.trackItemAction(
              section?.id || '',
              place.id,
              'place_card_view',
              'place',
              {
                place_name: place.name,
                shows_count: place.shows.length,
                source: 'map_marker'
              }
            );
            onPlaceClick(place);
          }
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
            activeMarkerRef.current = null;
          }
        }, { once: true });
      }

      place.shows.forEach(show => {
        const event = show.events[0];
        if (event.category) {
          const categoryId = createSafeEventId('category', event.category);
          const categoryButton = document.getElementById(categoryId);
          if (categoryButton) {
            categoryButton.addEventListener('click', (e) => {
              e.stopPropagation();
              analyticsService.trackEvent({
                event_type: 'filter',
                event_name: 'category_filter',
                event_data: {
                  section_id: section?.id,
                  filter_value: event.category,
                  source: 'map_marker'
                }
              });
              onCategorySelect(event.category);
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
                activeMarkerRef.current = null;
              }
            }, { once: true });
          }
        }
        if (event.type) {
          const typeId = createSafeEventId('type', event.type);
          const typeButton = document.getElementById(typeId);
          if (typeButton) {
            typeButton.addEventListener('click', (e) => {
              e.stopPropagation();
              analyticsService.trackEvent({
                event_type: 'filter',
                event_name: 'type_filter',
                event_data: {
                  section_id: section?.id,
                  filter_value: event.type,
                  source: 'map_marker'
                }
              });
              onTypeSelect(event.type);
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
                activeMarkerRef.current = null;
              }
            }, { once: true });
          }
        }
      });
    }, 0);
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const initMap = async () => {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        places.forEach(place => {
          bounds.extend({ lat: parseFloat(place.latitude), lng: parseFloat(place.longitude) });
        });
        bounds.extend({ lat: mainLocation.latitude, lng: mainLocation.longitude });

        const mapOptions: google.maps.MapOptions = {
          zoom: 12,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: true,
          clickableIcons: false
        };

        if (config.id_Gmap) {
          mapOptions.mapId = config.id_Gmap;
        }

        const map = new window.google.maps.Map(mapRef.current, mapOptions);
        mapInstanceRef.current = map;
        map.fitBounds(bounds);

        infoWindowRef.current = new window.google.maps.InfoWindow({
          disableAutoPan: false
        });

        map.addListener('click', () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
            activeMarkerRef.current = null;
          }
        });

        await createMarkers();

      } catch (error) {
        throw error;
      }
    };

    initMap().catch(error => {
      // Handle error
    });
  }, [isLoaded]);

  const createMarkers = async () => {
    if (!mapInstanceRef.current) return;

    try {
      if (mainMarkerRef.current) {
        mainMarkerRef.current.map = null;
      }
      markersRef.current.forEach(marker => marker.map = null);
      markersRef.current = [];

      // Create main marker (hotel)
      const mainMarkerElement = document.createElement('div');
      mainMarkerElement.innerHTML = `
        <div style="
          width: 54px;
          height: 54px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s ease-out;
        ">
          <svg viewBox="0 0 24 24" width="100%" height="100%">
            <path 
              fill="${config['major-color']}" 
              stroke="${config['light-color']}" 
              stroke-width="0.5"
              d="M12 2.25a7.76 7.76 0 0 0-7.75 7.75A12.84 12.84 0 0 0 12 22.47a2.58 2.58 0 0 0 1.29-.35 12.81 12.81 0 0 0 6.46-11.12A7.76 7.76 0 0 0 12 2.25zm2.75 11.75a.75.75 0 0 1-1.5 0v-2.25h-2.5v2.25a.75.75 0 0 1-1.5 0v-6a.75.75 0 0 1 1.5 0v2.25h2.5v-2.25a.75.75 0 0 1 1.5 0z"
            />
          </svg>
        </div>
      `;

      mainMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        map: mapInstanceRef.current,
        position: {
          lat: mainLocation.latitude,
          lng: mainLocation.longitude
        },
        title: mainLocation.name,
        content: mainMarkerElement,
        zIndex: 1000
      });

      // Gérer le z-index du marqueur principal au survol
      const mainMarkerInitialZIndex = mainMarkerRef.current.zIndex;
      mainMarkerElement.addEventListener('mouseenter', () => {
        if (mainMarkerRef.current) {
          mainMarkerRef.current.zIndex = 2000;
          mainMarkerElement.style.transform = 'scale(1.1)';
        }
      });

      mainMarkerElement.addEventListener('mouseleave', () => {
        if (mainMarkerRef.current) {
          mainMarkerRef.current.zIndex = mainMarkerInitialZIndex;
          mainMarkerElement.style.transform = 'scale(1)';
        }
      });

      mainMarkerRef.current.addListener('gmp-click', () => {
        if (infoWindowRef.current && mapInstanceRef.current && mainMarkerRef.current) {
          analyticsService.trackEvent({
            event_type: 'interaction',
            event_name: 'hotel_marker_click',
            event_data: {
              section_id: section?.id,
              hotel_name: mainLocation.name
            }
          });
          
          const content = `
            <div class="p-4 max-w-xs">
              ${config.url && config.image_infobulle ? `
                <a 
                  href="${config.url}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block w-full h-32 mb-3 cursor-pointer transition-opacity hover:opacity-90"
                >
                  <img 
                    src="${config.image_infobulle}" 
                    alt="${mainLocation.name}" 
                    class="w-full h-full object-cover rounded"
                  />
                </a>
              ` : ''}
              
              <h3 class="font-bold text-lg mb-3">${mainLocation.name}</h3>
              
              <div class="flex flex-col gap-2">
                ${config.url ? `
                  <a
                    href="${config.url}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="w-full px-4 py-2 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    style="background-color: ${config['major-color']}"
                  >
                    ${getTranslatedText('btn_more_info')}
                  </a>
                ` : ''}

                ${config.telephone ? `
                  <a
                    href="tel:${config.telephone}"
                    class="w-full px-4 py-2 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    style="background-color: ${config['major-color']}"
                  >
                    ${getTranslatedText('btn_call')}
                  </a>
                ` : ''}

                ${config.fulladress ? `
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.fulladress)}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="w-full px-4 py-2 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    style="background-color: ${config['major-color']}"
                  >
                    ${getTranslatedText('btn_itinerary')}
                  </a>
                ` : ''}

                ${config.booking ? `
                  <a
                    href="${config.booking}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="w-full px-4 py-2 text-white rounded text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    style="background-color: ${config['major-color']}"
                  >
                    ${getTranslatedText('btn_booking')}
                  </a>
                ` : ''}
              </div>
            </div>
          `;

          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapInstanceRef.current, mainMarkerRef.current);

          // Add event listeners for tracking
          window.setTimeout(() => {
            if (config.url) {
              const urlElements = document.querySelectorAll(`a[href="${config.url}"]`);
              urlElements.forEach(element => {
                element.addEventListener('click', () => {
                  analyticsService.trackEvent({
                    event_type: 'interaction',
                    event_name: 'hotel_info_click',
                    event_data: {
                      section_id: section?.id,
                      hotel_name: mainLocation.name,
                      url: config.url
                    }
                  });
                }, { once: true });
              });
            }

            if (config.telephone) {
              const telElement = document.querySelector(`a[href="tel:${config.telephone}"]`);
              if (telElement) {
                telElement.addEventListener('click', () => {
                  analyticsService.trackEvent({
                    event_type: 'interaction',
                    event_name: 'hotel_call_click',
                    event_data: {
                      section_id: section?.id,
                      hotel_name: mainLocation.name,
                      phone: config.telephone
                    }
                  });
                }, { once: true });
              }
            }

            if (config.fulladress) {
              const itineraryElement = document.querySelector(`a[href*="maps/dir"]`);
              if (itineraryElement) {
                telElement.addEventListener('click', () => {
                  analyticsService.trackEvent({
                    event_type: 'interaction',
                    event_name: 'hotel_directions_click',
                    event_data: {
                      section_id: section?.id,
                      hotel_name: mainLocation.name,
                      address: config.fulladress
                    }
                  });
                }, { once: true });
              }
            }

            if (config.booking) {
              const bookingElement = document.querySelector(`a[href="${config.booking}"]`);
              if (bookingElement) {
                bookingElement.addEventListener('click', () => {
                  analyticsService.trackEvent({
                    event_type: 'interaction',
                    event_name: 'hotel_booking_click',
                    event_data: {
                      section_id: section?.id,
                      hotel_name: mainLocation.name,
                      url: config.booking
                    }
                  });
                }, { once: true });
              }
            }
          }, 0);
        }
      });

      // Create place markers
      for (const place of places) {
        const firstShow = place.shows[0];
        const firstEvent = firstShow?.events[0];
        const categoryColor = firstEvent ? categoryColors.get(firstEvent.category) || config['medium-color'] : config['medium-color'];
        
        const markerElement = document.createElement('div');
        markerElement.innerHTML = `
          <div style="
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-center;
            cursor: pointer;
            transition: transform 0.2s ease-out;
            position: relative;
          ">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="100%" height="100%">
              <path 
                fill="${categoryColor}"
                stroke="${config['light-color']}"
                stroke-width="10"
                d="M384,64.8C305.6-6.6,184.2-1,112.8,77.4c-71.4,78.4-65.8,199.8,12.6,271.2,2,1.8,4.1,3.6,6.1,5.4,44.8,37.3,82.3,82.6,110.4,133.6,2.8,5.2,8.2,8.4,14.1,8.4,5.9,0,11.3-3.2,14.1-8.4l1.3-2.5c28.3-50.4,65.7-95.2,110.2-132.1,80.2-69.3,89-190.4,19.7-270.6-5.4-6.2-11.2-12.1-17.3-17.6Z"
              />
            </svg>
            ${place.shows.length > 1 ? `
              <div style="
                position: absolute;
                top: 42%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: ${config['light-color']};
                font-weight: bold;
                font-size: ${place.shows.length > 99 ? '11px' : '14px'};
                font-family: ${config.font_family || 'Arial'};
                text-align: center;
                line-height: 1;
              ">
                ${place.shows.length}
              </div>
            ` : ''}
          </div>
        `;

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          map: mapInstanceRef.current,
          position: { 
            lat: parseFloat(place.latitude), 
            lng: parseFloat(place.longitude) 
          },
          title: place.name,
          content: markerElement,
          zIndex: 1
        });

        // Gérer le z-index du marqueur au survol
        const markerInitialZIndex = marker.zIndex;
        markerElement.addEventListener('mouseenter', () => {
          marker.zIndex = 1500;
          markerElement.style.transform = 'scale(1.1)';
        });

        markerElement.addEventListener('mouseleave', () => {
          marker.zIndex = markerInitialZIndex;
          markerElement.style.transform = 'scale(1)';
        });

        marker.addListener('gmp-click', () => {
          if (infoWindowRef.current && mapInstanceRef.current) {
            analyticsService.trackEvent({
              event_type: 'interaction',
              event_name: 'place_marker_click',
              event_data: {
                section_id: section?.id,
                place_name: place.name,
                shows_count: place.shows.length
              }
            });

            if (activeMarkerRef.current === marker) {
              if (place.shows.length === 1) {
                onItemClick(place.shows[0].events[0]);
              } else {
                onPlaceClick(place);
              }
              infoWindowRef.current.close();
              activeMarkerRef.current = null;
              return;
            }

            activeMarkerRef.current = marker;

            const placeId = createSafeEventId('place', place.name);
            const content = place.shows.length === 1 
              ? createSingleShowContent(place.shows[0], placeId)
              : createMultipleShowsContent(place, placeId);

            infoWindowRef.current.setContent(content);
            infoWindowRef.current.open(mapInstanceRef.current, marker);

            setupInfoWindowEventListeners(placeId, place);
          }
        });

        markersRef.current.push(marker);
      }
    } catch (error) {
      // Handle error
    }
  };

  useEffect(() => {
    if (mapInstanceRef.current) {
      createMarkers();
    }
  }, [places, mainLocation, categoryColors, i18n.language]);

  if (!isLoaded) {
    return <LoadingIndicator config={config} translations={translations} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-red-50 text-red-600 p-4">
        <p className="text-center mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef} 
      className="w-full h-full"
      style={{ 
        position: 'relative',
        zIndex: 1
      }}
    />
  );
};

export default MapView;
export { MapView };